import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Search, ShieldCheck, Mail, KeyRound, RefreshCw, UserX, History,
  CheckCircle2, XCircle, Clock, CreditCard, Pause, Inbox, Activity, Ban,
} from "lucide-react";
import { DashboardShell, PageHead, EmptyState } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { journeyApi, apiError } from "@/lib/journeyApi";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function Dot({ ok, warn }) {
  const cls = ok ? "text-[#164C3B]" : warn ? "text-[#B45309]" : "text-[#9A8C7A]";
  const Icon = ok ? CheckCircle2 : warn ? Clock : XCircle;
  return <Icon className={`h-4 w-4 ${cls}`} />;
}

function Row({ label, value, ok, warn, icon: Icon, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#EFEAE1] py-2.5 last:border-0" data-testid={testid}>
      <span className="flex items-center gap-2 text-sm text-[#70584A]">
        {Icon ? <Icon className="h-4 w-4 text-[#A89A88]" /> : null}{label}
      </span>
      <span className="flex items-center gap-1.5 text-sm font-medium text-[#171717]">
        {ok === undefined ? null : <Dot ok={ok} warn={warn} />}
        <span>{value}</span>
      </span>
    </div>
  );
}

export default function AdminSupport() {
  const [allowed, setAllowed] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");
  const [audit, setAudit] = useState([]);
  const [requests, setRequests] = useState([]);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  useEffect(() => {
    journeyApi.isAdmin().then((a) => setAllowed(!!a.is_admin)).catch(() => setAllowed(false));
  }, []);

  const loadAudit = () =>
    journeyApi.adminAuditLog(25).then((d) => setAudit(d.entries || [])).catch(() => {});
  const loadRequests = () =>
    journeyApi.adminSupportRequests(25).then((d) => setRequests(d.requests || [])).catch(() => {});

  useEffect(() => { if (allowed) { loadAudit(); loadRequests(); } }, [allowed]);

  const lookup = async (e) => {
    e?.preventDefault?.();
    const q = email.trim().toLowerCase();
    if (!q || !q.includes("@")) return toast.error("Enter the customer's exact email address.");
    setLoading(true); setCard(null); setNotFound(false); setActivity([]);
    try {
      const data = await journeyApi.adminCustomerLookup(q);
      if (data.found === false) { setNotFound(true); }
      else {
        setCard(data);
        journeyApi.adminCustomerActivity(q).then((d) => setActivity(d.events || [])).catch(() => setActivity([]));
      }
    } catch (err) {
      toast.error(apiError(err, "Lookup failed."));
    } finally { setLoading(false); }
  };

  const runAction = async (fn, label) => {
    const r = reason.trim();
    if (r.length < 3) return toast.error("Add a short reason for this support action.");
    setBusy(label);
    try {
      const res = await fn({ email: email.trim().toLowerCase(), reason: r });
      toast.success(`${label}: ${res.outcome || "done"}`);
      await lookup();
      await loadAudit();
    } catch (err) {
      toast.error(apiError(err, `${label} failed.`));
    } finally { setBusy(""); }
  };

  const doRevokeAccess = async () => {
    const r = reason.trim();
    if (r.length < 3) { toast.error("Add a short reason for this support action."); return; }
    setConfirmRevoke(false);
    setBusy("Revoke access");
    try {
      const res = await journeyApi.adminRevokeAccess({ email: email.trim().toLowerCase(), reason: r });
      toast.success(res.outcome === "already_revoked" ? "Access already revoked" : "App access revoked");
      await lookup();
      await loadAudit();
    } catch (err) {
      toast.error(apiError(err, "Revoke access failed."));
    } finally { setBusy(""); }
  };

  if (allowed === null) {
    return <DashboardShell><PageHead title="Customer support" backToHome /><div className="h-40 animate-pulse rounded-2xl bg-[#F0EBE2]" /></DashboardShell>;
  }
  if (!allowed) {
    return (
      <DashboardShell>
        <PageHead title="Customer support" backToHome />
        <EmptyState icon={ShieldCheck} title="Not available" body="This founder support view is only available to administrators." testid="support-forbidden" />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <PageHead title="Customer support" sub="Exact-email lookup of non-financial account status, plus a few safe, audited recovery actions. No financial data is shown here." backToHome homeTo="/dashboard/admin/analytics" />

      <form onSubmit={lookup} className="flex flex-col gap-3 sm:flex-row" data-testid="support-lookup-form">
        <Input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="customer@email.com" autoComplete="off"
          className="flex-1 rounded-xl border-[#E5E0D9]" data-testid="support-email-input"
        />
        <Button type="submit" disabled={loading} className="rounded-xl bg-[#164C3B] text-white hover:bg-[#123b2e]" data-testid="support-lookup-button">
          <Search className="mr-2 h-4 w-4" />{loading ? "Looking up…" : "Look up"}
        </Button>
      </form>

      {notFound && (
        <div className="mt-6 rounded-2xl pe-surface p-6" data-testid="support-not-found">
          <p className="flex items-center gap-2 text-sm text-[#70584A]"><UserX className="h-4 w-4 text-[#A89A88]" /> No account found with that exact email address.</p>
        </div>
      )}

      {card && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5" data-testid="support-card">
          <div className="lg:col-span-3 rounded-2xl pe-surface p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Account status</p>
            <div className="mt-3">
              <Row label="Account" value={card.account?.status || "active"} ok testid="support-row-account" />
              <Row label="User ID" value={<span className="font-mono text-xs">{card.account?.user_id}</span>} testid="support-row-userid" />
              <Row label="Sign-in method" value={(card.account?.auth_providers || []).join(", ") || "password"} testid="support-row-auth" />
              <Row label="Created" value={fmt(card.account?.created_at)} testid="support-row-created" />
              <Row label="Email verified" value={card.email_verified ? "Verified" : "Not verified"} ok={card.email_verified} warn={!card.email_verified} icon={Mail} testid="support-row-verified" />
              <Row label="Onboarding" value={card.onboarding_complete ? "Complete" : "Incomplete"} ok={card.onboarding_complete} warn={!card.onboarding_complete} testid="support-row-onboarding" />
              <Row label="Entitlement" value={card.entitlement?.active ? "Active" : (card.entitlement?.status || "None")} ok={card.entitlement?.active} warn={!card.entitlement?.active} testid="support-row-entitlement" />
              {card.entitlement?.revoked_reason ? <Row label="Revoked reason" value={card.entitlement.revoked_reason} warn testid="support-row-revoked" /> : null}
              <Row label="Confirmed payment" value={card.payment?.confirmed_paid ? "Paid" : "No paid record"} ok={card.payment?.confirmed_paid} warn={!card.payment?.confirmed_paid} icon={CreditCard} testid="support-row-payment" />
              {card.refund?.has_purchase ? (
                <>
                  <Row label="Purchase time" value={fmt(card.refund.purchase_timestamp)} testid="support-row-refund-purchase" />
                  <Row label="Refund eligible until" value={fmt(card.refund.refund_eligible_until)} testid="support-row-refund-until" />
                  <Row label="Refund status" value={{eligible: "Eligible (within 14 days)", expired: "Window expired", refunded: "Refunded", disputed: "Disputed", unknown: "Unknown (legacy)"}[card.refund.eligibility] || card.refund.eligibility}
                    ok={card.refund.eligibility === "eligible"} warn={["refunded", "disputed", "expired"].includes(card.refund.eligibility)} testid="support-row-refund-status" />
                  <Row label="Refunded" value={card.refund.refunded ? "Yes" : "No"} warn={card.refund.refunded} testid="support-row-refunded" />
                </>
              ) : null}
              <Row label="Purchase activated" value={card.activation?.claimed ? "Claimed" : "Not claimed"} ok={card.activation?.claimed} warn={!card.activation?.claimed} testid="support-row-activation" />
              <Row label="Paused" value={card.pause?.paused ? `Paused (${fmt(card.pause.paused_at)})` : "Active"} ok={!card.pause?.paused} warn={card.pause?.paused} icon={Pause} testid="support-row-pause" />
              <Row label="Latest Edit" value={card.latest_edit ? `${card.latest_edit.type} · ${card.latest_edit.status}` : "None yet"} testid="support-row-edit" />
              {card.latest_edit ? <Row label="Edit updated" value={fmt(card.latest_edit.at)} testid="support-row-edit-at" /> : null}
              <Row label="Reminders scheduled" value={`${card.reminders?.scheduled_count ?? 0} scheduled · ${card.reminders?.sent_count ?? 0} sent`} icon={Clock} testid="support-row-reminders" />
              {card.reminders?.next ? <Row label="Next reminder" value={`${card.reminders.next.type} · ${fmt(card.reminders.next.at)}`} testid="support-row-next-reminder" /> : null}
            </div>
            <p className="mt-4 text-xs text-[#A89A88]">No income, debt, savings, goal, allocation or any financial amount is shown or logged here.</p>
          </div>

          <div className="lg:col-span-2 rounded-2xl pe-surface p-6" data-testid="support-actions">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]">Recovery actions</p>
            <label className="mt-3 block text-sm text-[#70584A]">Reason (required, recorded in the audit log)</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. customer emailed, never got verify link" className="mt-1.5 rounded-xl border-[#E5E0D9]" data-testid="support-reason-input" />
            <div className="mt-4 space-y-2.5">
              <Button onClick={() => runAction(journeyApi.adminResendVerification, "Resend verification")} disabled={!!busy} variant="outline" className="w-full justify-start rounded-xl border-[#E5E0D9]" data-testid="support-resend-verification">
                <Mail className="mr-2 h-4 w-4 text-[#164C3B]" /> Resend verification email
              </Button>
              <Button onClick={() => runAction(journeyApi.adminResendPasswordReset, "Send password reset")} disabled={!!busy} variant="outline" className="w-full justify-start rounded-xl border-[#E5E0D9]" data-testid="support-resend-reset">
                <KeyRound className="mr-2 h-4 w-4 text-[#164C3B]" /> Send password-reset link
              </Button>
              <Button onClick={() => runAction(journeyApi.adminReconcileEntitlement, "Reconcile entitlement")} disabled={!!busy} variant="outline" className="w-full justify-start rounded-xl border-[#E5E0D9]" data-testid="support-reconcile">
                <RefreshCw className="mr-2 h-4 w-4 text-[#164C3B]" /> Re-run entitlement reconcile
              </Button>
            </div>
            <p className="mt-4 text-xs text-[#A89A88]">Reconcile only restores access when a confirmed-paid transaction already exists. Access is never granted otherwise.</p>

            {card.revocation?.available ? (
              card.revocation?.refund_state === "dispute" ? (
                <div className="mt-6 rounded-xl border border-[#E7CFC6] bg-[#FBF3F0] p-4" data-testid="support-revoke-block">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A14A32]">Dispute / chargeback</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#70584A]">This payment has a verified dispute or chargeback. Revoking access will remove the customer's entitlement to use The Paycheck Edit™. Their account, payment history, dispute record, and required audit records will remain preserved.</p>
                  <Button onClick={() => setConfirmRevoke(true)} disabled={!!busy} className="mt-3 w-full justify-start rounded-xl bg-[#9B2C14] text-white hover:bg-[#82240f]" data-testid="support-revoke-access">
                    <Ban className="mr-2 h-4 w-4" /> Revoke app access
                  </Button>
                </div>
              ) : (
                <div className="mt-6 rounded-xl border border-[#E7CFC6] bg-[#FBF3F0] p-4" data-testid="support-revoke-block">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#A14A32]">Verified full refund</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#70584A]">This payment has been fully refunded. Revoking access will remove the customer's entitlement to use The Paycheck Edit™. Their account, payment history, refund record, and required audit records will remain preserved.</p>
                  <Button onClick={() => setConfirmRevoke(true)} disabled={!!busy} className="mt-3 w-full justify-start rounded-xl bg-[#9B2C14] text-white hover:bg-[#82240f]" data-testid="support-revoke-access">
                    <Ban className="mr-2 h-4 w-4" /> Revoke app access
                  </Button>
                </div>
              )
            ) : card.revocation?.already_revoked ? (
              <div className="mt-6 rounded-xl border border-[#E5E0D9] bg-[#F7F4EE] p-4" data-testid="support-revoke-already">
                <p className="flex items-center gap-2 text-sm text-[#70584A]"><Ban className="h-4 w-4 text-[#A89A88]" /> App access revoked{card.entitlement?.revoked_source ? ` · ${card.entitlement.revoked_source}` : ""}{card.entitlement?.revoked_at ? ` · ${fmt(card.entitlement.revoked_at)}` : ""}.</p>
              </div>
            ) : card.revocation?.refund_state === "refund_not_complete" ? (
              <div className="mt-6 rounded-xl border border-[#E5E0D9] bg-[#F7F4EE] p-4" data-testid="support-refund-incomplete">
                <p className="flex items-center gap-2 text-sm text-[#70584A]"><Ban className="h-4 w-4 text-[#A89A88]" /> Refund not complete — this payment is not fully refunded, so access cannot be revoked.</p>
              </div>
            ) : card.refund?.refunded && !card.entitlement?.active ? (
              <div className="mt-6 rounded-xl border border-[#E5E0D9] bg-[#F7F4EE] p-4" data-testid="support-revoke-already">
                <p className="flex items-center gap-2 text-sm text-[#70584A]"><Ban className="h-4 w-4 text-[#A89A88]" /> App access revoked for this refunded customer.</p>
              </div>
            ) : null}
          </div>
        </div>
      )}

      <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
        <AlertDialogContent data-testid="revoke-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>REVOKE CUSTOMER ACCESS?</AlertDialogTitle>
            <AlertDialogDescription>
              {card?.revocation?.refund_state === "dispute"
                ? "This payment has a verified dispute or chargeback. Revoking access will remove the customer's paid entitlement to The Paycheck Edit™. Transaction, dispute, entitlement-history, audit, and other required records will remain preserved."
                : "This customer has a verified full refund. Revoking access will remove their paid access to The Paycheck Edit™. Transaction, refund, entitlement-history, audit, and other required records will remain preserved."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRevokeAccess} className="bg-[#9B2C14] text-white hover:bg-[#82240f]" data-testid="revoke-confirm">Revoke access</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {card && (
        <div className="mt-8 rounded-2xl pe-surface p-6" data-testid="support-activity">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]"><Activity className="h-4 w-4" /> Account activity</p>
          {activity.length === 0 ? (
            <p className="mt-3 text-sm text-[#70584A]">No recorded activity for this account.</p>
          ) : (
            <ol className="mt-3 space-y-2">
              {activity.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-3 border-b border-[#EFEAE1] py-2 text-sm last:border-0" data-testid={`support-activity-${i}`}>
                  <span className="text-[#171717]">{a.label}</span>
                  <span className="shrink-0 text-xs text-[#A89A88]">{fmt(a.at)}</span>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-4 text-xs text-[#A89A88]">A privacy-safe behavioral timeline from recorded events. No income, debt, savings, goal names, balances, allocation or any financial amount is shown.</p>
        </div>
      )}

      <div className="mt-8 rounded-2xl pe-surface p-6" data-testid="support-queue">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]"><Inbox className="h-4 w-4" /> Support requests</p>
        {requests.length === 0 ? (
          <p className="mt-3 text-sm text-[#70584A]">No support requests yet.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {requests.map((r, i) => (
              <div key={r.id || i} className="border-b border-[#EFEAE1] pb-3 last:border-0" data-testid={`support-request-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-[#171717]">{r.subject} <span className="ml-1 rounded-full bg-[#DDE7DF] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#164C3B]">{r.category}</span></span>
                  <span className="flex items-center gap-2 text-xs text-[#A89A88]">
                    {r.emailed ? <span className="text-[#164C3B]">notified</span> : <span className="text-[#B45309]">store-only</span>}
                    · {fmt(r.created_at)}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-[#525252] whitespace-pre-wrap">{r.message}</p>
                <p className="mt-1 text-xs text-[#A89A88]">{r.name} · {r.email}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 rounded-2xl pe-surface p-6" data-testid="support-audit">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#A89A88]"><History className="h-4 w-4" /> Recent recovery actions</p>
        {audit.length === 0 ? (
          <p className="mt-3 text-sm text-[#70584A]">No recovery actions recorded yet.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {audit.map((a, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-2 border-b border-[#EFEAE1] py-2 text-sm last:border-0">
                <span className="text-[#171717]">{a.action} · <span className="text-[#70584A]">{a.outcome}</span></span>
                <span className="font-mono text-xs text-[#A89A88]">{a.target_user_id} · {fmt(a.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
